import clsx from "clsx";
import {ChangeEvent, FC, useEffect, useState} from "react";
import {useTranslation} from "react-i18next";
import {Combobox} from "@headlessui/react";
import {ICON_TYPE, Icon} from "../library/Icon";
import InputLabel from "../library/InputLabel";

type GoogleAddressComponent = {
   types: string[];
   long_name?: string;
   short_name?: string;
};

/** City-like component types, most specific / standard first. */
export const CITY_COMPONENT_TYPE_PRIORITY = [
   "locality",
   "sublocality",
   "postal_town",
   "sublocality_level_1",
   "sublocality_level_2",
   "sublocality_level_3",
   "sublocality_level_4",
   "sublocality_level_5",
   "neighborhood",
   "administrative_area_level_3",
] as const;

export const extractCityFromComponents = (
   addressComponents: GoogleAddressComponent[] | undefined
): string | undefined => {
   if (!addressComponents?.length) {
      return undefined;
   }

   for (const type of CITY_COMPONENT_TYPE_PRIORITY) {
      for (const component of addressComponents) {
         if (component.types?.includes(type) && component.long_name) {
            return component.long_name;
         }
      }
   }

   return undefined;
};

export const ParseGoogleAddress = (
   placeDetails: {address_components: GoogleAddressComponent[]},
   useShortState: boolean
): AutoCompleteAddress | undefined => {
   if (!placeDetails) {
      return;
   }

   let address: AutoCompleteAddress = {};

   let streetCode = "";
   let streetName = "";

   const {address_components} = placeDetails;
   address_components?.forEach((address_component) => {
      const address_type = address_component.types;

      if (address_type.includes("street_number")) {
         streetCode = address_component.long_name || "";
      } else if (address_type.includes("route")) {
         streetName = address_component.long_name || "";
      } else if (address_type.includes("postal_code")) {
         address.zipCode = address_component.long_name;
      } else if (address_type.includes("country")) {
         address.country = address_component.short_name;
      } else if (address_type.includes("administrative_area_level_1")) {
         address.state = useShortState
            ? address_component.short_name
            : address_component.long_name;
         address.stateCode = address_component.short_name;
      }
   });

   address.city = extractCityFromComponents(address_components);

   if (streetCode || streetName)
      address.addressLine1 = `${streetCode ? streetCode + " " : ""}${streetName}`;
   // address.formatted_address = formatted_address;
   return address;
};

type GoogleAutoCompleteProps = {
   id: string;
   name: string;
   label?: string;
   placeholder?: string;
   onAddressSelect: (address: Address) => void;
   className?: string;
   required?: boolean;
   value?: string;
   /** External error state - shows validation error when true */
   error?: boolean;
};

type Suggestion = {place_id: string; description: string};

export type AutoCompleteAddress = {
   addressLine1?: string;
   zipCode?: string;
   country?: string;
   state?: string;
   stateCode?: string;
   city?: string;
};

export type Address = AutoCompleteAddress & {isComplete: boolean};

export const GetAddressValue = (address: AutoCompleteAddress) => {
   return `${address.addressLine1}, ${address.city}, ${address.state}, ${address.zipCode}`;
};

const GoogleAutoComplete: FC<GoogleAutoCompleteProps> = ({
   label,
   name,
   id,
   placeholder,
   onAddressSelect,
   className,
   required,
   value,
   error,
}) => {
   const {t: ct} = useTranslation("common");

   const [query, setQuery] = useState("");
   const [selectedAddress, setSelectedAddress] = useState<
      (AutoCompleteAddress & {isComplete: boolean}) | undefined
   >();
   const [suggestions, setSuggestions] = useState<Suggestion[]>([]);
   const [hasBeenTouched, setHasBeenTouched] = useState(false);

   const fetchPlaceAutocomplete = async () => {
      if (query.length > 2) {
         try {
            const response = await fetch(`/api/autocomplete/${query}`, {
               credentials: "include",
            });

            if (!response.ok) {
               setSuggestions([]);
               return;
            }

            const _suggestions = await response.json();

            if (!Array.isArray(_suggestions)) {
               setSuggestions([]);
               return;
            }

            setSuggestions(_suggestions);
         } catch (error) {
            console.error(error);
            setSuggestions([]);
         }
      } else {
         setSuggestions([]);
      }
   };

   useEffect(() => {
      fetchPlaceAutocomplete();
   }, [query]);

   useEffect(() => {
      if (selectedAddress) {
         onAddressSelect(selectedAddress);
      }
   }, [selectedAddress]);

   const onSuggestionSelect = async (suggestion: Suggestion) => {
      if (suggestion?.place_id) {
         try {
            const response = await fetch(`/api/places/${suggestion.place_id}`, {
               credentials: "include",
            });

            if (!response.ok) {
               console.error("Failed to fetch place details");
               return;
            }

            const _address = await response.json();

            if (!_address) {
               console.error("No address data returned");
               return;
            }

            const formattedAddress = ParseGoogleAddress(_address, true);

            setSelectedAddress({...formattedAddress, isComplete: true});
            setSuggestions([]);
         } catch (error) {
            console.error("Error fetching place details:", error);
         }
      }
   };

   const handleInputChange = (event: ChangeEvent<HTMLInputElement>) => {
      setQuery(event.target.value);
      setSelectedAddress({
         addressLine1: event.target.value,
         city: selectedAddress?.city || "",
         state: selectedAddress?.state || "",
         zipCode: selectedAddress?.zipCode || "",
         country: selectedAddress?.country || "US",
         stateCode: selectedAddress?.stateCode || "",
         isComplete: false,
      });
   };

   // Field is complete when:
   // 1. User has selected a complete address from suggestions (selectedAddress.isComplete === true)
   // 2. OR there's a value from parent AND user hasn't started editing yet (selectedAddress is undefined)
   const isComplete =
      selectedAddress?.isComplete === true ||
      ((value?.length ?? 0) > 0 && selectedAddress === undefined);

   // Show validation error when:
   // 1. Field has been touched OR external error prop is true
   // 2. AND field is not complete
   const shouldShowValidation = (hasBeenTouched || error) && !isComplete;

   return (
      <div className={clsx(className)}>
         {label && (
            <InputLabel
               htmlFor={id}
               value={required ? `${label} *` : label}
               tooltipText={ct("selectAddressTooltip")}
            />
         )}
         <Combobox
            as="fieldset"
            className={clsx("relative")}
            onChange={(value: any) => {
               onSuggestionSelect(value);
            }}
            value={
               value ||
               (selectedAddress?.isComplete
                  ? GetAddressValue(selectedAddress)
                  : selectedAddress?.addressLine1) ||
               query
            }
            name={name}
         >
            <span
               className={clsx({
                  hidden: !isComplete,
               })}
            >
               <Icon
                  icon="circle-check"
                  className={clsx(
                     "absolute",
                     "right-2",
                     "top-1/2",
                     "-translate-y-1/2",
                     "text-green-500",
                     "z-10"
                  )}
                  type={ICON_TYPE.SOLID}
               />
            </span>
            <Combobox.Input
               placeholder={placeholder}
               className={clsx(
                  "bg-white dark:bg-gray_comp px-3 py-2 border rounded-lg w-full text-sm",
                  shouldShowValidation ? "border-red-500" : "border-300"
               )}
               onChange={handleInputChange}
               onBlur={() => setHasBeenTouched(true)}
               displayValue={(val: string) => val || query}
            />

            {suggestions.length > 0 && (
               <Combobox.Options
                  className={clsx(
                     "absolute",
                     "z-10",
                     "mt-1",
                     "max-h-60",
                     "w-full",
                     "overflow-auto",
                     "rounded-md",
                     "bg-panel",
                     "py-1",
                     "text-base",
                     "shadow-lg",
                     "ring-1",
                     "ring-black",
                     "ring-opacity-5",
                     "focus:outline-none",
                     "sm:text-sm"
                  )}
               >
                  {suggestions.map((suggestion) => (
                     <Combobox.Option
                        key={suggestion.place_id}
                        value={suggestion}
                        className={({active}) =>
                           clsx(
                              "relative py-2 pr-9 pl-3 cursor-default select-none",
                              active ? "bg-blue-500 text-white" : "text-800"
                           )
                        }
                     >
                        {() => (
                           <span className={clsx("block truncate")}>{suggestion.description}</span>
                        )}
                     </Combobox.Option>
                  ))}
               </Combobox.Options>
            )}
         </Combobox>
         <p
            className={clsx("mt-2 text-red-600 dark:text-red-500 text-sm", {
               hidden: !shouldShowValidation,
            })}
         >
            {!query && !selectedAddress?.addressLine1 && !value
               ? ct("fieldRequired")
               : ct("pleaseSelectAValidAddress")}
         </p>
      </div>
   );
};

export default GoogleAutoComplete;
